import { ButtonType, CheckBoxWithLabelWidget, DialogFooterButtonsWidget, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { defaultMemoize } from 'reselect';

import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { getAppStore } from '~src/app/AppStore';
import { SessionServerSelectors,SessionSiteSelectors,SessionUserSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { assertUnreachable } from '~src/ts/helpers/TypescriptHelpers';
import { OutputFileFormat,SubscriptionFlow, SubscriptionFlowDetails,SubscriptionFlowMetadata } from '~src/ts/ServerApi/types/SubscriptionFlow';

import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { cssClass } from '../../style';
import { L10n } from '../../ts/L10n/L10n';
import { FlowBaseInfo } from '../../ts/models/FlowInfo';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { IFlowRunSpecOutputStepViewModel } from '../../ts/ServerApi/types/IFlowRunSpecOutputStepViewModel';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { Flows } from '../resources/Flows';
import { RunFlowTasks } from '../resources/RunFlowTasks';
import { FlowErrors } from '../services/FlowErrors';
import { FlowParameter } from '../types/FlowParameters';
import { Recipient } from '../types/Recipient';
import { areFlowParametersEnabled,parameterMapToArray,separateParameters } from '../utils/FlowParametersUtils';
import { DEFAULT_SHOULD_ATTACH_OUTPUT_DATA_TO_EMAIL,DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE, DEFAULT_SHOULD_SHOW_FIRST_N_ROWS, getEmailRecipientGroupIds,getEmailRecipientUserIds,isSelectedOutputAPublishedDatasource, isValidEmailTemplate } from '../utils/FlowSubscriptionUtil';
import { FlowOutputsSelection } from './FlowOutputsSelection';
import { FlowParametersActions } from './FlowParametersDialogContent';
import { FlowParametersDropdown, FlowParametersDropdownProps } from './FlowParametersDropdown';
import { FlowRunSchedulePicker } from './FlowRunSchedulePicker';
import { FlowSubscriptionButtonWithDialog } from './FlowSubscriptionButtonWithDialog';
import { FlowSubscriptionCount } from './FlowSubscriptionCount';

const cssPrefix = 'RunFlowTaskDialogContent';

const scrollWrapper = cssClass(cssPrefix, 'scrollWrapper', {
  height: 385,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
});

const smallPadding = cssClass(cssPrefix, 'smallPadding', {
  padding: `0 ${CommonStyles.Sizes.SmallSpace}px`,
});

const dialogMessage = cssClass(cssPrefix, 'dialogMessage', {
  marginBottom: `${TabStyles.Spacing.LargeSpace}px`,
  wordWrap: 'break-word'
});

const footerWrapper = cssClass(cssPrefix, 'footerWrapper', {
  paddingTop: CommonStyles.Sizes.MediumSpace,
});

const emailWhenDoneWrapper = cssClass(cssPrefix, 'emailWhenDoneWrapper', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const parametersWrapper = cssClass(cssPrefix, 'parametersWrapper', {
  padding: `0 ${CommonStyles.Sizes.SmallSpace}px 8px ${CommonStyles.Sizes.SmallSpace}px`,
});

const countsWrapper = cssClass(cssPrefix, 'countsWrapper', {
  height: 18,
});

export type RunFlowTaskDialogContentProps = {
  taskInfo?: RunFlowTaskInfo; // Used for editing a task
  flowInfo?: FlowBaseInfo;    // Used for creating a task
  singleSelectedOutputId?: string; // Used for creating a task starting with a single selected output
  dismiss: () => void;
};

export type RunFlowTaskDialogContentState = {
  selectedSchedule: string;
  selectAll: boolean;
  outputsSelected: { readonly [key: string]: boolean };
  outputsIsIncremental: { readonly [key: string]: boolean };
  isRunAllIncremental: boolean;
  outputs: ReadonlyArray<FlowOutputStepInfo>;
  isRunning: boolean;
  shouldSendEmailWhenDone: boolean;
  shouldSendToMe: boolean;
  subjectValue: string;
  messageValue: string;
  emailRecipients: Recipient[];
  shouldIncludeOutputData: boolean;
  selectedOutputForSubscription: string | null; // id of the output
  shouldShowFirstNRows: boolean;
  shouldAttachOutputDataToEmail: boolean;
  outputFileFormat: OutputFileFormat;
  shouldIncludeLinkToDatasource: boolean;
  parametersMap: {[key: string]: string};
  subscriptionCount: number | null;
  canCreateSubscription: boolean;
  requiredParameters: FlowParameter[];
  additionalParameters: FlowParameter[];
};

export class RunFlowTaskDialogContent extends React.PureComponent<RunFlowTaskDialogContentProps, RunFlowTaskDialogContentState> {

  private toasterService: ToasterService;

  constructor(props: RunFlowTaskDialogContentProps) {
    super(props);
    this.toasterService = ToasterService.instance;
    const outputsSelected: {[key: string]: boolean} = {};
    const outputsIsIncremental: {[key: string]: boolean} = {};
    this.state = {
      outputs: null,
      selectedSchedule: this.props.taskInfo ? this.props.taskInfo.schedule.id : '',
      selectAll: false,
      outputsSelected: outputsSelected,
      outputsIsIncremental: outputsIsIncremental,
      isRunAllIncremental: null,
      isRunning: false,
      shouldSendEmailWhenDone: false,
      shouldSendToMe: true,
      subjectValue: Messages.flowSubscriptionsSubjectDefault({FLOW_NAME: this.props.flowInfo?.name}),
      messageValue: '',
      emailRecipients: [],
      shouldIncludeOutputData: false,
      selectedOutputForSubscription: null,
      shouldShowFirstNRows: false,
      shouldAttachOutputDataToEmail: false,
      outputFileFormat: 'CSV',
      shouldIncludeLinkToDatasource: false,
      subscriptionCount: null,
      canCreateSubscription: false,
      requiredParameters: [],
      additionalParameters: [],
      parametersMap: {},
    };
    Flows.getOutputStepInfos(this.props.flowInfo ? this.props.flowInfo.id : this.props.taskInfo.flow.id).then(items => {
      if (this.props.taskInfo) {
        // Editing a task
        if (areFlowParametersEnabled() && this.props.taskInfo.flowOutputSteps.length > 0) {
          let outputParams: FlowParameter[] = [];
          this.props.taskInfo.flowOutputSteps.forEach(output => {
            if (output.relevantParameters.length > 0) {
              outputParams = outputParams.concat(output.relevantParameters);
            }
          });
          let map: {[key: string]: string} = {};
          this.props.taskInfo.relevantParameterRuns.forEach((run) => {
            map[run.parameterId] = run.overrideValue;
          });
          this.setState({
            ...separateParameters(outputParams),
            parametersMap: map,
          });
        }
        RunFlowTasks.getSubscriptionFlow(this.props.taskInfo.id).then((result) => {
          if (result?.subscriptionFlow?.subscriptionFlowMetadata?.subscriberCount > 0) {
            this.setState({subscriptionCount: result.subscriptionFlow.subscriptionFlowMetadata.subscriberCount});
          }
        });
        if (this.props.taskInfo.runAllOutputs) {
          this.setState({
            outputs: items,
            selectAll: true,
            outputsSelected: this.getAllOutputsSelected(items),
            isRunAllIncremental: this.props.taskInfo.isRunAllIncremental,
          });

        } else {
          const existingSelectedOutputs: {[key: string]: boolean} = {};
          const existingOutputsIsIncremental: {[key: string]: boolean} = {};
          this.props.taskInfo.flowOutputSteps.forEach((step) => {
            existingSelectedOutputs[step.id] = true;
            existingOutputsIsIncremental[step.id] = step.isIncremental;
          });
          this.setState({
            outputs: items,
            outputsSelected: existingSelectedOutputs,
            outputsIsIncremental: existingOutputsIsIncremental
          });
        }
      } else {
        // Creating a new task
        if (areFlowParametersEnabled() && this.props.flowInfo.relevantParameters.length > 0) {
          this.setState({...separateParameters(this.props.flowInfo.relevantParameters)});
        }
        const flowOutputSubscriptionsEnabledOnSite: boolean = SessionSiteSelectors.isFlowOutputSubscriptionsEnabled(getAppStore().getState());
        if (flowOutputSubscriptionsEnabledOnSite) {
          Flows.actions(props.flowInfo.id).then((result) => {
            this.setState({canCreateSubscription: result.createSubscription});
          });
        }
        // by default we want to include all outputs
        // in the case we are opening from the overview page, we have a single output preselected
        this.setState({
          outputs: items,
          selectAll: !this.props.singleSelectedOutputId,
          outputsSelected: this.props.singleSelectedOutputId ?
            {[this.props.singleSelectedOutputId]: true}
            : this.getAllOutputsSelected(items)
        });
      }
    });
}

  private onSelectSchedule = (value: string) => {
    this.setState({selectedSchedule: value});
  }

  private getAllOutputsSelected = (outputs: ReadonlyArray<FlowOutputStepInfo>) => {
    const outputsSelected: {[key: string]: boolean} = {};
    outputs.forEach(output => outputsSelected[output.id] = true);
    return outputsSelected;
  }

  private handleIncludeAll = () => {
    if (this.state.selectAll) {
      this.setState({
        selectAll: false,
        outputsSelected: {},
        selectedOutputForSubscription: null,
        shouldIncludeOutputData: false,
        shouldIncludeLinkToDatasource: false,
      });
    } else {
      this.setState({
        selectAll: true,
        outputsSelected: this.getAllOutputsSelected(this.state.outputs)
      });
    }
  }

  private toggleSelectAll = () => {
    if (this.getSelectedOutputsCount() === this.state.outputs.length) {
      this.setState({
        outputsSelected: {},
        selectedOutputForSubscription: null,
        shouldIncludeOutputData: false,
        shouldIncludeLinkToDatasource: false,
      });
    } else {
      this.setState({
        outputsSelected: this.getAllOutputsSelected(this.state.outputs)
      });
    }
  }

  private handleOutputChange = (id: string) => {
    if (this.state.outputsSelected[id] && id === this.state.selectedOutputForSubscription) {
      // then its going to be unselected so update flow subscription as appropriate
      this.setState({
        selectedOutputForSubscription: null,
        shouldIncludeOutputData: false,
        shouldIncludeLinkToDatasource: false,
      });
    }
    this.setState({
      outputsSelected: {
        ...this.state.outputsSelected,
        [id]: !this.state.outputsSelected[id]
      }
    });
  }

  private handleOutputIsIncrementalChange = (id: string, isIncremental: boolean) => {
    if (this.state.selectAll) {
      this.setState({
        isRunAllIncremental: isIncremental,
      });
    } else {
      this.setState({
        outputsIsIncremental: {
          ...this.state.outputsIsIncremental,
          [id]: isIncremental
        },
      });
    }
  }

  private handleEmailWhenDoneToggled = () => {
    this.setState({shouldSendEmailWhenDone: !this.state.shouldSendEmailWhenDone});
  }

  private getFlowAndOutputStepParams(){
    const outputIds: string[] = [];
    const flowRunSpecOutputStepParams: IFlowRunSpecOutputStepViewModel[] = [];
    const isRunAllIncremental = this.state.isRunAllIncremental;

    for (const output in this.state.outputsSelected) {
      if (this.state.outputsSelected[output]) {
        outputIds.push(output);
        if (!this.state.selectAll) {
          flowRunSpecOutputStepParams.push({
            isIncremental: this.state.outputsIsIncremental[output],
            flowOutputStepId: parseInt(output, 10)
          });
        }
      }
    }

    return {outputIds, flowRunSpecOutputStepParams, isRunAllIncremental};
  }

  private getSelectedOutputStepInfos = (): FlowOutputStepInfo[] => {
    if (this.state.outputs === null) {
      return [];
    }
    const outputStepInfos: FlowOutputStepInfo[] = this.state.outputs.filter((output) => {
      return this.state.outputsSelected[output.id];
    });
    return outputStepInfos;
  }

  private getSubscriptionFlow = (): SubscriptionFlow => {
    const currentUserId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    const userIds = this.state.shouldSendToMe ?
      getEmailRecipientUserIds(this.state.emailRecipients, currentUserId)
      : getEmailRecipientUserIds(this.state.emailRecipients);
    const subscriptionFlowMetadata: SubscriptionFlowMetadata = {
      targetId: this.props.flowInfo.id,
      subject: this.state.subjectValue,
      message: this.state.messageValue,
      userIds: userIds
    };
    const subscriptionFlowDetails: SubscriptionFlowDetails = {
      groupIds: getEmailRecipientGroupIds(this.state.emailRecipients),
      includeOutputData: this.state.shouldIncludeOutputData,
      outputId: this.state.selectedOutputForSubscription,
      includeLinkToOutputData: this.state.shouldIncludeLinkToDatasource,
      // showRowsInEmailBody: this.state.shouldShowFirstNRows, TODO: TFSID 1322014 disabled until maintenance release 2020.4.1
      showRowsInEmailBody: false,
      attachOutputData: this.state.shouldAttachOutputDataToEmail,
      attachedOutputDataFormat: this.state.shouldAttachOutputDataToEmail ? this.state.outputFileFormat : null,
    };
    const subscriptionFlow: SubscriptionFlow = {
      subscriptionFlowMetadata: subscriptionFlowMetadata,
      subscriptionFlowDetails: subscriptionFlowDetails,
    };
    return subscriptionFlow;
  }

  private handleCreateSchedule = () => {
    this.setState({isRunning: true});
    const {outputIds, flowRunSpecOutputStepParams, isRunAllIncremental} = this.getFlowAndOutputStepParams();

    Flows.createRunFlowTask(
      this.props.flowInfo.id,
      this.state.selectedSchedule,
      this.state.selectAll,
      isRunAllIncremental,
      outputIds,
      flowRunSpecOutputStepParams,
      this.state.shouldSendEmailWhenDone,
      this.state.shouldSendEmailWhenDone ? this.getSubscriptionFlow() : null,
      Object.keys(this.state.parametersMap).length > 0 ? parameterMapToArray(this.state.parametersMap) : null,
    ).then(result => {
      this.showToastMessages(result);
      this.setState({isRunning: false});
      getAppStore().dispatch(requestSpaceContents());
    });
  }

  private handleEditSchedule = () => {
    this.setState({isRunning: true});
    if (this.props.taskInfo.schedule.id !== this.state.selectedSchedule) {
      RunFlowTasks.setSchedule([this.props.taskInfo.id], this.state.selectedSchedule)
      .then(result => {
        if (result.errors && result.errors.length > 0) {
          this.showErrorMessages(result);
        }
        this.setOutputSteps().then(() => getAppStore().dispatch(requestSpaceContents()));
      });
    } else {
      this.setOutputSteps().then(() => getAppStore().dispatch(requestSpaceContents()));
    }
  }

  private setOutputSteps = (): Promise<any> => {
    const { outputIds, flowRunSpecOutputStepParams, isRunAllIncremental } = this.getFlowAndOutputStepParams();
    return RunFlowTasks.setOutputSteps(
      this.props.taskInfo.id,
      this.state.selectAll,
      isRunAllIncremental,
      outputIds,
      flowRunSpecOutputStepParams,
      Object.keys(this.state.parametersMap).length > 0 ? parameterMapToArray(this.state.parametersMap) : null,
    ).then(result => {
      this.showToastMessages(result, true);
      this.setState({ isRunning: false });
    });
  }

  private showToastMessages = (result: IResult, isEdit?: boolean) => {
    if (result.errors && result.errors.length > 0) {
      this.showErrorMessages(result);
    } else {
      this.toasterService.pop({
        type: ToastType.Info,
        text: isEdit ? Messages.editFlowTaskSuccessful() : Messages.createFlowTask(),
        testId: `tb-toast-flow-task-success`
      });
      this.closeDialog();
    }
  }
  private showErrorMessages = (result: IResult) => {
    result.errors.forEach((error: IError) => {
      this.toasterService.pop({
        type: ToastType.Error,
        text: FlowErrors.errorMessageFor({ errorCode: error.code }, false) || error.message,
        testId: `tb-toast-${error.code}`
      });
    });
  }

  private footerButtons = (state: RunFlowTaskDialogContentState) => (
    <DialogFooterButtonsWidget
      buttons={[{
        handleClick: this.closeDialog,
        buttonType: ButtonType.Outline,
        children: Messages.cancel(),
        testId: 'schedule-flow-run-footer-cancel'
      }, {
        handleClick: this.props.flowInfo ? this.handleCreateSchedule : this.handleEditSchedule,
        buttonType: ButtonType.Go,
        children: this.props.flowInfo ? Messages.scheduleFlowRun() : Messages.linkedTasksApply(),
        testId: 'schedule-flow-run-footer-go',
        disabled: this.isButtonDisabled()
      }]}
      testId={'schedule-flow-run-footer'}
    />
  )

  private memoizedFooterButtons = defaultMemoize(this.footerButtons);

  // Button is disabled if: Dialog was submitted and waiting results, there is no selected schedules or outputs,
  private isButtonDisabled = (): boolean => {
    return this.state.isRunning || !this.state.selectedSchedule || this.areAnyOutputsSelected();
  }

  private areAnyOutputsSelected = (): boolean => {
    return !(Object.keys(this.state.outputsSelected).some(key => this.state.outputsSelected[key]));
  }

  private getSelectedOutputsCount = (): number => {
    let count = 0;
    Object.keys(this.state.outputsSelected).forEach((output) => this.state.outputsSelected[output] ? count++ : null);
    return count;
  }

  private closeDialog = () => {
    this.props.dismiss && this.props.dismiss();
  }

  private handleShouldSendToMeToggled = () => {
    this.setState({shouldSendToMe: !this.state.shouldSendToMe});
  }

  private handleShouldIncludeOutputDataToggled = () => {
    this.setState({shouldIncludeOutputData: !this.state.shouldIncludeOutputData});
  }

  private handleShouldShowFirstNRowsToggled = () => {
    this.setState({shouldShowFirstNRows: !this.state.shouldShowFirstNRows});
  }

  private handleShouldAttachOutputDataToEmailToggled = () => {
    this.setState({shouldAttachOutputDataToEmail: !this.state.shouldAttachOutputDataToEmail});
  }

  private handleShouldIncludeLinkToDatasourceToggled = () => {
    this.setState({shouldIncludeLinkToDatasource: !this.state.shouldIncludeLinkToDatasource});
  }

  private onSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.setState({subjectValue: value});
  }

  private onMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    this.setState({messageValue: value});
  }

  private setOutputSelectionToDefaultsForOutput = (outputId: string) => {
    const isOnPremEnvironment: boolean = !SessionServerSelectors.isOnlineEnvironment(getAppStore().getState());
    const isSelectedOutputPublishedDatasource: boolean = isSelectedOutputAPublishedDatasource(outputId, this.state.outputs);
    if (isOnPremEnvironment) {
      if (isSelectedOutputPublishedDatasource) {
        this.setState({
          shouldAttachOutputDataToEmail: false,
          shouldIncludeLinkToDatasource: DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE,
          shouldShowFirstNRows: DEFAULT_SHOULD_SHOW_FIRST_N_ROWS
        });
      } else { // output that is not a published datasource
        this.setState({
          shouldIncludeLinkToDatasource: false,
          shouldShowFirstNRows: DEFAULT_SHOULD_SHOW_FIRST_N_ROWS,
          shouldAttachOutputDataToEmail: DEFAULT_SHOULD_ATTACH_OUTPUT_DATA_TO_EMAIL
        });
      }
    } else { // online
      this.setState({
        shouldIncludeLinkToDatasource: DEFAULT_SHOULD_INCLUDE_LINK_TO_DATASOURCE
      });
    }
  }

  private onSelectedOutputForSubscriptionChange = (value: string) => {
    if (value === this.state.selectedOutputForSubscription) {
      return;
    }
    this.setOutputSelectionToDefaultsForOutput(value);
    this.setState({selectedOutputForSubscription: value});
  }

  private handleRecipientsChange = (recipients: Recipient[]) => {
    this.setState({emailRecipients: recipients});
  }

  private onOutputFileFormatChange = (value: OutputFileFormat) => {
    this.setState({outputFileFormat: value});
  }

  private validateEmailTemplate = () => {
    if (!isValidEmailTemplate(this.state.shouldSendToMe, this.state.emailRecipients, this.state.subjectValue, this.state.shouldIncludeOutputData, this.state.selectedOutputForSubscription)) {
      // desired behavior is that when the flow subscription template is invalid, shouldSendEmailWhenDone
      // should be unchecked
      this.setState({shouldSendEmailWhenDone: false});
    }
  }

  private parametersActionHandler(action: FlowParametersActions) {
    switch(action.type) {
      case 'FlowParametersSetParameters':
        this.setState({parametersMap: action.payload});
        break;
      default:
        assertUnreachable(action.type);
    }
  }

  private renderParameters(): JSX.Element {
    if (!areFlowParametersEnabled() || (this.state.requiredParameters.length + this.state.additionalParameters.length) === 0) {
      return null;
    }
    const parametersProps: FlowParametersDropdownProps = {
      requiredParameters: this.state.requiredParameters,
      additionalParameters: this.state.additionalParameters,
      overrideValues: this.state.parametersMap,
      actionHandler: this.parametersActionHandler.bind(this),
    };
    return (
      <div className={parametersWrapper}>
        <FlowParametersDropdown {...parametersProps}/>
      </div>
    );
  }

  public render(): JSX.Element {
    const flowOutputSubscriptionsEnabledOnSite = SessionSiteSelectors.isFlowOutputSubscriptionsEnabled(getAppStore().getState());

    return (
      <>
        <div className={scrollWrapper} >
          <div className={smallPadding}>
            <div className={dialogMessage}>{Messages.scheduleFlowChooseScheduleMessage(
              { FLOW_NAME: L10n.elideIfLong(this.props.flowInfo ? this.props.flowInfo.name : this.props.taskInfo.flow.name) })}</div>
            <FlowRunSchedulePicker
              scheduleId={this.state.selectedSchedule}
              onSelect={this.onSelectSchedule}
            />
          </div>
          <FlowOutputsSelection
            outputs={this.state.outputs}
            selectAll={this.state.selectAll}
            outputsSelected={this.state.outputsSelected}
            outputsIsIncremental={this.state.outputsIsIncremental}
            isRunAllIncremental={this.state.isRunAllIncremental}
            handleChildChange={this.handleOutputChange}
            handleChildIsIncrementalChange={this.handleOutputIsIncrementalChange}
            toggleIncludeAll={this.handleIncludeAll}
            toggleSelectAll={this.toggleSelectAll}
          />
          {this.renderParameters()}
          { flowOutputSubscriptionsEnabledOnSite && this.props.flowInfo && this.state.canCreateSubscription ?
            <div className={emailWhenDoneWrapper}>
              <CheckBoxWithLabelWidget
                label={Messages.flowSubscriptionsCheckboxLabel()}
                checked={this.state.shouldSendEmailWhenDone}
                handleChange={this.handleEmailWhenDoneToggled}
                testId={'flow-subscription-checkbox-test-id'}
              />
              <FlowSubscriptionButtonWithDialog
                outputs={this.getSelectedOutputStepInfos()}
                shouldSendToMe={this.state.shouldSendToMe}
                subjectValue={this.state.subjectValue}
                messageValue={this.state.messageValue}
                shouldSendEmailWhenDone={this.state.shouldSendEmailWhenDone}
                shouldIncludeOutputData={this.state.shouldIncludeOutputData}
                shouldShowFirstNRows={this.state.shouldShowFirstNRows}
                shouldAttachOutputDataToEmail={this.state.shouldAttachOutputDataToEmail}
                selectedOutputForSubscription={this.state.selectedOutputForSubscription}
                outputFileFormat={this.state.outputFileFormat}
                shouldIncludeLinkToDatasource={this.state.shouldIncludeLinkToDatasource}
                onDialogClose={this.validateEmailTemplate}
                onShouldSendToMeToggled={this.handleShouldSendToMeToggled}
                onShouldIncludeOutputDataToggled={this.handleShouldIncludeOutputDataToggled}
                onAttachOutputDataToEmailToggled={this.handleShouldAttachOutputDataToEmailToggled}
                onShowFirstNRowsToggled={this.handleShouldShowFirstNRowsToggled}
                onIncludeLinkToDatasourceToggled={this.handleShouldIncludeLinkToDatasourceToggled}
                onSubjectChange={this.onSubjectChange}
                onMessageChange={this.onMessageChange}
                emailRecipients={this.state.emailRecipients}
                onRecipientsChange={this.handleRecipientsChange}
                onOutputFileFormatChange={this.onOutputFileFormatChange}
                onSelectedOutputForSubscriptionChange={this.onSelectedOutputForSubscriptionChange}
              />
            </div>
            : flowOutputSubscriptionsEnabledOnSite && !this.props.flowInfo && this.state.subscriptionCount ?
            <div className={countsWrapper}>
              <FlowSubscriptionCount count={this.state.subscriptionCount} showCheckmark={true} flowId={this.props.taskInfo.flowId}/>
            </div>
            : null
          }
        </div>
        <div className={footerWrapper}>
          {this.memoizedFooterButtons(this.state)}
        </div>
      </>
    );
  }
}
